import { useCallback } from 'react';

import registerMetric from '../../api/services/stats.service';
import { getSearchData } from '../../services/search-client';
import { setCurrentLayout, useLayout } from '../components/context/layout';
import { setLoad, setSearch } from '../components/context/search';
import { useStaticProps } from '../components/context/static-props';
import { ERROR_CATCHER, LAYOUTS, FILTERS } from '../constants';
import { completeStateSearch } from '../lib/complete-search-state';
import { trackEvent } from '../lib/melidata';
import redirectTo from '../utils/redirect-to';
import { registerErrorMetrics } from '../utils/register-error-metrics';
import { useSearch } from './context';
import { setEshopsEnvironment } from '../components/context/eshop-environment';
import { setLocationSearchInitialState } from '../components/context/location-search/location-search';
import { removePolygonParamToUrl, appendPolygonParamToUrl } from '../components/map/helper';

const equalHostname = (href) => {
  const url = new URL(href);

  return url.hostname === window.location.hostname;
};

const handlePolygonLocationForSPA = (href, layoutCurrent) => {
  if (layoutCurrent !== LAYOUTS.TYPE_MAP) {
    return href;
  }

  if (typeof window === 'undefined' || !window.location?.search) {
    return href;
  }

  try {
    const searchParams = new URLSearchParams(window.location.search);
    const polygonLocation = searchParams.get(FILTERS.IDS.POLYGON_LOCATION);

    if (!polygonLocation) {
      return href;
    }

    return appendPolygonParamToUrl(href, polygonLocation);
  } catch (error) {
    return href;
  }
};

export const useSearchClient = () => {
  const { layout_options } = useSearch();
  const { queryParams } = useStaticProps();
  const { currentLayout } = useLayout();

  const updateSearchStates = useCallback(
    (newSearchContext) => {
      const { storefront, layout_options: newLayoutOptions } = newSearchContext || {};
      const appearance = storefront?.appearance;
      const newCurrentLayout = newLayoutOptions?.current;

      if (newCurrentLayout && currentLayout?.id !== newCurrentLayout) {
        setCurrentLayout(newCurrentLayout);
      }

      const isEshopsEnvironment = !appearance?.hidden && appearance?.children?.length > 0;

      setEshopsEnvironment(isEshopsEnvironment);
      setLocationSearchInitialState(newSearchContext);
      setSearch(completeStateSearch(newSearchContext));
    },
    [currentLayout?.id],
  );

  const redirectWithContext = useCallback(
    (href) => {
      const cleanedUrl = removePolygonParamToUrl(queryParams);

      redirectTo(href, {
        useLinkBuilder: true,
        linkBuilderConfig: {
          isInternal: true,
          queryParams: cleanedUrl,
          currentLayout,
        },
      });
    },
    [queryParams, currentLayout],
  );

  const handleResponse = useCallback(
    async (href) => {
      try {
        setLoad(true);
        window.scrollTo({ top: 0 });

        const newSearchContext = await getSearchData({ url: href });

        if (!newSearchContext || newSearchContext?.rescue) {
          redirectWithContext(href);

          return;
        }

        window.history.pushState(null, null, href);
        updateSearchStates(newSearchContext);
        setLoad(false);
        trackEvent({
          path: '/search',
          clean: true,
          type: 'view',
          event_data: {},
          ...newSearchContext?.melidata_track,
        });
      } catch (error) {
        registerErrorMetrics({
          errorType: 'try_catch_error',
          errorCatcher: ERROR_CATCHER.TRY_CATCH,
          errorMessage: error?.message,
          errorServiceName: 'search-client',
        });
        redirectWithContext(href);
      }
    },
    [updateSearchStates, redirectWithContext],
  );

  const handleLinkSPA = useCallback(
    ({ event = null, href }) => {
      event?.preventDefault();

      const finalHref = handlePolygonLocationForSPA(href, layout_options?.current);
      const hasPolygonLocation = finalHref !== href;

      const isSpaEnabled = layout_options?.spa_enabled;
      const isSameHostname = equalHostname(finalHref);

      if (isSpaEnabled || hasPolygonLocation) {
        setLoad(true);

        if (!isSameHostname) {
          // Esta metrica nos indica que en una SPA se intento ir a otro subdominio
          registerMetric({
            key: 'search-client.subdomain',
            tags: {
              error_type: 'different_subdomain',
            },
          });
        }
      }

      if (!isSpaEnabled || !isSameHostname) {
        redirectWithContext(finalHref);

        return;
      }

      handleResponse(finalHref);
    },
    [handleResponse, layout_options, redirectWithContext],
  );

  return { handleLinkSPA };
};
